// Usage/billing shapes. These are read-only projections computed from the
// per-run usage events emitted on SSE `usage` frames and persisted by the API.
import { Type } from '@sinclair/typebox';
import { ProviderSchema } from './providers.js';
export const UsageBucketSchema = Type.Object({
    // ISO day (YYYY-MM-DD) or ISO hour (YYYY-MM-DDTHH) — caller picks the grain.
    bucket: Type.String({ minLength: 10, maxLength: 13 }),
    inputTokens: Type.Integer({ minimum: 0 }),
    outputTokens: Type.Integer({ minimum: 0 }),
    costUsd: Type.Number({ minimum: 0 }),
    runs: Type.Integer({ minimum: 0 }),
}, { $id: 'UsageBucket', additionalProperties: false });
export const UsageByModelSchema = Type.Object({
    provider: ProviderSchema,
    model: Type.String({ minLength: 1, maxLength: 128 }),
    inputTokens: Type.Integer({ minimum: 0 }),
    outputTokens: Type.Integer({ minimum: 0 }),
    costUsd: Type.Number({ minimum: 0 }),
    runs: Type.Integer({ minimum: 0 }),
}, { $id: 'UsageByModel', additionalProperties: false });
export const UsageQuerySchema = Type.Object({
    from: Type.String({ format: 'date-time' }),
    to: Type.String({ format: 'date-time' }),
    grain: Type.Union([Type.Literal('hour'), Type.Literal('day')]),
    agentId: Type.Optional(Type.String({ format: 'uuid' })),
}, { $id: 'UsageQuery', additionalProperties: false });
// Response shape for GET /v1/usage (Phase 4.8). Combines the tenant's current
// billing-window subscription state with the live usage totals (sourced from
// ai_call_logs, not usage_events — the latter trails by one flush window).
// agentId/agentName are nullable to accommodate the "deleted agent" bucket:
// ai_call_logs.agent_id is `ON DELETE SET NULL` so the call log survives the
// agent's deletion (it is the billing source-of-truth). Per-agent usage rolls
// up into a single null-id bucket the dashboard renders as "(deleted agent)"
// — without it, the per-agent sum silently undercounts the tenant total.
export const UsageByAgentSchema = Type.Object({
    agentId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    agentName: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    inputTokens: Type.Integer({ minimum: 0 }),
    outputTokens: Type.Integer({ minimum: 0 }),
    costUsd: Type.Number({ minimum: 0 }),
    runs: Type.Integer({ minimum: 0 }),
}, { $id: 'UsageByAgent', additionalProperties: false });
export const UsagePeriodTotalsSchema = Type.Object({
    inputTokens: Type.Integer({ minimum: 0 }),
    outputTokens: Type.Integer({ minimum: 0 }),
    costUsd: Type.Number({ minimum: 0 }),
    // Total provider calls — successful + failed. Drives the "Runs" stat
    // card and the failure-rate visibility the operator gets from seeing
    // total calls vs. successful messages.
    runs: Type.Integer({ minimum: 0 }),
    // Successful calls only (`error_class IS NULL`). The plan-message-cap
    // is enforced against this number — failed turns still consume tokens
    // but do not count toward the message quota. The dashboard plan meter
    // binds to this so the bar matches what the gate actually rejects on.
    messages: Type.Integer({ minimum: 0 }),
}, { $id: 'UsagePeriodTotals', additionalProperties: false });
// Plan caps surfaced alongside totals so the dashboard can render the meter
// (used / cap) without a second round trip. `null` cap means "unlimited"
// (enterprise tier later) — the dashboard renders "—" instead of a bar.
export const UsagePlanLimitsSchema = Type.Object({
    monthlyMessageCap: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    monthlyTokenCap: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    dailyCostSoftUsd: Type.Number({ minimum: 0 }),
    dailyCostHardUsd: Type.Number({ minimum: 0 }),
    includedAgents: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    // Mirrors `plan_limits.allowed_models`. The dashboard's agent-create form
    // intersects this with MODELS_BY_PROVIDER per provider to render only the
    // models the tenant can actually pick — server gate is the authority but
    // surfacing the list client-side avoids a 403 round-trip on submit.
    allowedModels: Type.Array(Type.String(), { default: [] }),
}, { $id: 'UsagePlanLimits', additionalProperties: false });
export const UsageSummarySchema = Type.Object({
    // 'no_subscription' when the tenant hasn't completed checkout — totals are
    // still reported so dev / pre-billing tenants can see what they've burned.
    plan: Type.Object({
        code: Type.String(),
        status: Type.String(),
        currentPeriodStart: Type.String({ format: 'date-time' }),
        currentPeriodEnd: Type.String({ format: 'date-time' }),
        frozen: Type.Boolean(),
        // Populated only when frozen=true; otherwise null.
        frozenReason: Type.Union([Type.String(), Type.Null()]),
        // ISO timestamp at which the subscription will auto-cancel. Set by
        // Stripe when the customer schedules a cancellation via Customer
        // Portal (`cancel_at_period_end=true`). Sub stays `status=active`
        // until that date — the dashboard surfaces the schedule with a
        // "Cancels on <date>" banner and gates plan switching until Resume.
        // Cleared (null) once the customer Resumes via either the dashboard
        // button or the Portal's "Don't cancel subscription" action.
        cancelAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    }),
    // Sums for the current Stripe billing window. Driven by ai_call_logs
    // gte(currentPeriodStart) — matches what the reconciliation job ships
    // to Stripe meters.
    period: UsagePeriodTotalsSchema,
    byAgent: Type.Array(UsageByAgentSchema),
    // Daily UTC buckets across [currentPeriodStart, currentPeriodEnd). Empty
    // days are omitted (the dashboard backfills zeros so the line is continuous).
    series: Type.Array(UsageBucketSchema),
    // Null when plan.code === 'no_subscription' (dev / pre-billing tenants
    // have no caps to compare against — the meter UI hides itself).
    limits: Type.Union([UsagePlanLimitsSchema, Type.Null()]),
    // Number of non-sample agents whose persisted model is no longer in
    // plan_limits.allowed_models — typically because the tenant downgraded
    // after configuring agents on a higher tier. Drives the Home banner
    // ("N agents need attention") + the per-row badge on /agents. 0 when
    // everything's clean.
    planMismatchAgentCount: Type.Integer({ minimum: 0, default: 0 }),
    // Number of non-sample agents soft-disabled by plan-downgrade
    // reconciliation (migration 0025). Surfaced on /v1/usage so the
    // dashboard's Home banner can prompt the customer to review which
    // ones to re-enable on upgrade or permanently delete. 0 when
    // nothing's been downgraded into an over-cap state.
    disabledAgentCount: Type.Integer({ minimum: 0, default: 0 }),
    // Number of KB documents soft-excluded from retrieval by the same
    // reconciliation. Excluded docs still occupy storage (still count
    // toward the bytes cap) but are skipped by the retrieval JOIN.
    excludedKbDocCount: Type.Integer({ minimum: 0, default: 0 }),
}, { $id: 'UsageSummary', additionalProperties: false });
// POST /v1/billing/portal — caller passes a return URL; we mint a Stripe
// Billing Portal session and redirect the dashboard to it.
export const CreatePortalSessionInputSchema = Type.Object({
    returnUrl: Type.String({ format: 'uri', minLength: 1, maxLength: 2048 }),
}, { $id: 'CreatePortalSessionInput', additionalProperties: false });
export const CreatePortalSessionResultSchema = Type.Object({
    url: Type.String({ format: 'uri' }),
}, { $id: 'CreatePortalSessionResult', additionalProperties: false });
// POST /v1/billing/checkout — caller picks a target paid plan; we mint a
// Stripe Checkout Session against the tenant's existing customer that
// includes the base recurring price + the metered overage price as line
// items. Stripe drives the rest (payment method capture, subscription
// create); the resulting `customer.subscription.created` webhook flips
// `subscriptions.plan_code` once the user finishes the flow.
//
// `planCode` is restricted to the paid tiers — there's no checkout for
// `free` (downgrade is a portal action, not a checkout). Successful
// callers receive the Stripe-hosted URL to redirect to; failures map to
// 402 (no Stripe customer, plan misconfigured, etc.).
export const CreateCheckoutSessionInputSchema = Type.Object({
    planCode: Type.Union([Type.Literal('starter'), Type.Literal('pro')]),
    successUrl: Type.String({ format: 'uri', minLength: 1, maxLength: 2048 }),
    cancelUrl: Type.String({ format: 'uri', minLength: 1, maxLength: 2048 }),
}, { $id: 'CreateCheckoutSessionInput', additionalProperties: false });
export const CreateCheckoutSessionResultSchema = Type.Object({
    url: Type.String({ format: 'uri' }),
}, { $id: 'CreateCheckoutSessionResult', additionalProperties: false });
// POST /v1/billing/subscription — switch plan on an existing active sub.
//
// We do this server-side rather than via Stripe Customer Portal because
// Portal cannot switch subs that have a metered overage line item — a
// documented Stripe limitation. The portal stays for cancellation +
// payment-method updates.
//
// Same plan-code constraint as checkout: only paid tiers. Free is reached
// by cancelling via portal, which routes through customer.subscription.deleted.
//
// `keepActiveAgentIds` is the customer's modal-time selection when the
// downgrade would put the tenant over the target plan's agent cap. Empty/
// missing on a non-downgrade or under-cap downgrade — the route only
// validates and consumes it when reconciliation needs a non-default pick.
// On over-cap without selection the route returns 400 `selection_required`
// carrying the preview body so the dashboard can re-render the modal.
export const ChangeSubscriptionPlanInputSchema = Type.Object({
    planCode: Type.Union([Type.Literal('starter'), Type.Literal('pro')]),
    keepActiveAgentIds: Type.Optional(Type.Array(Type.String({ format: 'uuid' }), { maxItems: 256 })),
}, { $id: 'ChangeSubscriptionPlanInput', additionalProperties: false });
// GET /v1/billing/subscription/preview?targetPlan=starter — snapshot of the
// over-cap diff for a given target plan. Drives the dashboard's downgrade
// modal: the customer sees which agents/KB docs would be auto-disabled +
// which the system pre-selected to keep active, and confirms or adjusts.
//
// activeCount and overage live alongside the lists so a small payload (no
// over-cap) doesn't force the dashboard to re-derive. cap is null when the
// target plan is unlimited (Pro for agents). preselectedActive flags the
// most-recently-modified N items the modal should default-check.
export const DowngradePreviewSchema = Type.Object({
    currentPlan: Type.String({ minLength: 1 }),
    targetPlan: Type.String({ minLength: 1 }),
    agents: Type.Object({
        activeCount: Type.Integer({ minimum: 0 }),
        cap: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
        overage: Type.Integer({ minimum: 0 }),
        list: Type.Array(Type.Object({
            id: Type.String({ format: 'uuid' }),
            name: Type.String(),
            model: Type.String(),
            updatedAt: Type.String({ format: 'date-time' }),
            preselectedActive: Type.Boolean(),
        })),
    }),
    kbDocuments: Type.Object({
        activeCount: Type.Integer({ minimum: 0 }),
        countCap: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
        totalBytes: Type.Integer({ minimum: 0 }),
        totalBytesCap: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
        overage: Type.Object({
            count: Type.Integer({ minimum: 0 }),
            bytes: Type.Integer({ minimum: 0 }),
        }),
        list: Type.Array(Type.Object({
            id: Type.String({ format: 'uuid' }),
            filename: Type.String(),
            bytes: Type.Integer({ minimum: 0 }),
            updatedAt: Type.String({ format: 'date-time' }),
            preselectedActive: Type.Boolean(),
        })),
    }),
}, { $id: 'DowngradePreview', additionalProperties: false });
export const ChangeSubscriptionPlanResultSchema = Type.Object({
    // Stripe-side status after the update. The DB reflects the swap once
    // customer.subscription.updated webhook lands (typically <1s).
    status: Type.String({ minLength: 1 }),
}, { $id: 'ChangeSubscriptionPlanResult', additionalProperties: false });
// POST /v1/billing/subscription/resume — clear cancel_at_period_end on a
// scheduled-to-cancel subscription. No input body — the caller's identity
// is the auth context, and there's only one decision (resume yes/no).
//
// Why this exists alongside Stripe Portal's "Don't cancel subscription"
// button: both are valid Resume paths. Customers who cancel from inside
// the dashboard expect a Resume button there too; sending them to the
// Portal for a Resume-only action is poor UX. Both endpoints converge on
// the same `customer.subscription.updated` webhook with cancel_at:null.
export const ResumeSubscriptionResultSchema = Type.Object({
    // Stripe subscription status after the call. Always 'active' on success.
    status: Type.String({ minLength: 1 }),
}, { $id: 'ResumeSubscriptionResult', additionalProperties: false });
// GET /v1/billing/invoices — paginated list of Stripe invoices for the
// tenant's customer. We mirror Stripe's shape verbatim (cents, ISO-4217
// lowercase currency, unix-seconds created), letting the dashboard format.
export const InvoiceSummarySchema = Type.Object({
    id: Type.String({ minLength: 1 }),
    status: Type.String({ minLength: 1 }),
    amountDue: Type.Integer({ minimum: 0 }),
    amountPaid: Type.Integer({ minimum: 0 }),
    currency: Type.String({ minLength: 3, maxLength: 3 }),
    created: Type.Integer({ minimum: 0 }),
    hostedInvoiceUrl: Type.Union([Type.String(), Type.Null()]),
    invoicePdf: Type.Union([Type.String(), Type.Null()]),
    number: Type.Union([Type.String(), Type.Null()]),
}, { $id: 'InvoiceSummary', additionalProperties: false });
export const ListInvoicesResultSchema = Type.Object({
    invoices: Type.Array(InvoiceSummarySchema),
}, { $id: 'ListInvoicesResult', additionalProperties: false });
//# sourceMappingURL=usage.js.map